import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import {Fragment, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import TablePageSizeSelector from './TablePageSizeSelector';
import useResponsivePageSize from './useResponsivePageSize';
import useIsMobile from './useIsMobile';
import TableSearchBar from './TableSearchBar';
import TableHeader from './TableHeader';
import Pagination from './pagination/Pagination';
import Loading from '../Loading';
import Level3Title from '../title/Level3Title';
import PaginationDetail from './pagination/PaginationDetail';
import {Link} from 'react-router-dom';
import styles from './TableMetronic.module.scss';
import clsx from 'clsx';

interface TableMetronicProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  pageSize?: number;
  isLoading?: boolean;
  defaultSorting?: SortingState;
  noRowsMessage?: string;
  pagination?: boolean;
  /**
   * Server-driven pagination: `data` holds only the current page, fetched by
   * the caller. Requires `pageSize`, `pageCount` and `totalItems`; the table
   * calls `onPageChange` instead of slicing rows itself.
   */
  manualPagination?: boolean;
  pageIndex?: number;
  pageCount?: number;
  totalItems?: number;
  onPageChange?: (pageIndex: number) => void;
  searchBar?: boolean;
  smallSearchBar?: boolean;
  title?: string;
  caption?: string;
  elementName?: string;
  elementNamePlural?: string;
  toolbar?: JSX.Element;
  toolbarEnd?: JSX.Element;
  subtitle?: JSX.Element;
  autoResetPage?: boolean;
  customLinkTitle?: string;
  customLinkRoute?: string;
  showCardHeader?: boolean;
  stackedHeader?: boolean;

  rowClickable?: (row: T) => boolean; // si une ligne est cliquable
  onRowClick?: (row: T) => void;      // Callback au clic sur une ligne
  // Pagination serveur (opt-in) : si manualPagination est activé, `data` ne contient
  // que la page courante, et la pagination/tri par page-model est déléguée au parent.
  paginationState?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  rowCount?: number;

  getRowId?: (row: T) => string; // identifiant stable de ligne (utilisé pour l'expand)
  renderRowDetail?: (row: T) => JSX.Element | null; // contenu à afficher sous la ligne quand elle est dépliée, null si la ligne n'est pas dépliable

  renderMobileCard?: (row: T) => JSX.Element; // si fourni, remplace le tableau par une liste de cartes sous mobileBreakpoint
  mobileBreakpoint?: number; // largeur en dessous de laquelle renderMobileCard est utilisé (défaut 768px)
}

const TableMetronic = <T,>({
  data,
  columns,
  pageSize,
  isLoading,
  defaultSorting,
  noRowsMessage,
  pagination,
  manualPagination = false,
  pageIndex,
  pageCount,
  totalItems,
  onPageChange,
  searchBar,
  smallSearchBar = false,
  title,
  elementName,
  elementNamePlural,
  toolbar,
  toolbarEnd,
  subtitle,
  autoResetPage,
  customLinkTitle,
  customLinkRoute,
  showCardHeader = true,
  stackedHeader = false,
  caption,

  rowClickable,
  onRowClick,

  paginationState,
  onPaginationChange,
  rowCount,
  getRowId,
  renderRowDetail,
  renderMobileCard,
  mobileBreakpoint = 768,
}: TableMetronicProps<T>): JSX.Element => {
  const intl = useIntl();
  const [sorting, setSorting] = useState<SortingState>(defaultSorting ?? []);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const responsivePageSize = useResponsivePageSize();
  const isMobile = useIsMobile(mobileBreakpoint);
  const showMobileCards = isMobile && renderMobileCard !== undefined;

  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    if (!renderRowDetail) return columns;
    const expandColumn: ColumnDef<T> = {
      id: '__expandToggle',
      header: '',
      size: 40,
      enableSorting: false,
      meta: {noClick: true},
      cell: ({row}) => {
        const detail = renderRowDetail(row.original);
        if (!detail) return null;
        const isExpanded = expandedRowIds.has(row.id);
        return (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-active-light-primary"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedRowIds((prev) => {
                const next = new Set(prev);
                if (next.has(row.id)) {
                  next.delete(row.id);
                } else {
                  next.add(row.id);
                }
                return next;
              });
            }}
          >
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
          </button>
        );
      },
    };
    return [expandColumn, ...columns];
  }, [columns, renderRowDetail, expandedRowIds]);

  const table = useReactTable({
    data: data,
    columns: tableColumns,
    state: {
      sorting: sorting,
      ...(manualPagination && paginationState ? {pagination: paginationState} : {}),
    },
    initialState: {
      pagination: {
        pageSize: pageSize ?? responsivePageSize,
      },
    },
    manualPagination,
    // `onPaginationChange` (and `pageCount`) must be entirely absent for non-manual
    // tables too, same reason as `state.pagination` above: react-table merges options
    // with its own defaults via object spread, so a key present with value `undefined`
    // overwrites the built-in `onPaginationChange` default and turns setPageIndex/
    // nextPage/previousPage/setPageSize into silent no-ops — pagination renders and
    // is clickable, but nothing ever happens.
    ...(manualPagination
      ? {
          pageCount,
          onPaginationChange: (updater: Updater<PaginationState>) => {
            const current = {pageIndex: pageIndex ?? 0, pageSize: pageSize ?? responsivePageSize};
            const next = typeof updater === 'function' ? updater(current) : updater;
            onPageChange?.(next.pageIndex);
          },
        }
      : {}),
    onSortingChange: setSorting,
    ...(manualPagination ? {onPaginationChange} : {}),
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    pageCount: manualPagination ? (pageCount ?? -1) : undefined,
    debugTable: process.env.NODE_ENV === 'development',
    // see https://github.com/TanStack/table/issues/4280, function below is a fix to react table number columns bug
    globalFilterFn: (row, columnId, filterValue) => {
      const safeValue = (() => {
        const value = row.getValue(columnId);
        return typeof value === 'number' ? String(value) : value;
      })();
      // @ts-ignore: safeValue is of type unknown
      return safeValue?.toLowerCase().includes(filterValue.toLowerCase());
    },
    autoResetPageIndex: autoResetPage,
  });

  useEffect(() => {
    if (pageSize === undefined && !manualPagination) {
      table.setPageSize(responsivePageSize);
    }
  }, [responsivePageSize, pageSize, manualPagination, table]);

  // Revenir à la page 1 à chaque recherche : sinon, une recherche effectuée
  // depuis une page avancée peut ne retourner aucune ligne (page hors bornes
  // du résultat filtré) alors que des résultats existent bien en page 1.
  const handleGlobalFilterChange = (value: string) => {
    table.setGlobalFilter(value);
    if (!manualPagination) {
      table.setPageIndex(0);
    }
  };

  return (
    <>
      <div className="card">
        {showCardHeader &&
          (stackedHeader ? (
            <div className="card-header border-0 pt-6 d-block">
              {title && (
                <div className="card-title mb-4">
                  <Level3Title title={title} />
                </div>
              )}
              <div className="d-flex flex-wrap align-items-center gap-5">
                {toolbar}
                {searchBar !== false && (
                  <div className={styles.searchBarWrapper}>
                    <TableSearchBar
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                      small={smallSearchBar}
                    />
                  </div>
                )}
                {toolbarEnd && (
                  <div className={searchBar !== false ? undefined : 'ms-auto'}>{toolbarEnd}</div>
                )}
                {customLinkTitle && customLinkRoute && (
                  <Link
                    to={customLinkRoute}
                    title={customLinkTitle}
                    key={customLinkTitle.toLowerCase().trim()}
                  >
                    {customLinkTitle}
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="card-header border-0 pt-6">
              <div className="card-title">{title && <Level3Title title={title} />} </div>
              <div className="card-toolbar flex-row-fluid justify-content-end gap-5">
                {searchBar !== false && (
                  <div className={styles.searchBarWrapper}>
                    <TableSearchBar
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                      small={smallSearchBar}
                    />
                  </div>
                )}
                {customLinkTitle && customLinkRoute && (
                  <Link
                    to={customLinkRoute}
                    title={customLinkTitle}
                    key={customLinkTitle.toLowerCase().trim()}
                  >
                    {customLinkTitle}
                  </Link>
                )}
                {toolbar}
              </div>
            </div>
          ))}

        {subtitle && <div className="card-header border-0"> {subtitle} </div>}
        <div className="table-responsive table-loading">
          {isLoading && (
            <div className="table-loading-message">
              <Loading />
            </div>
          )}
          <div className="card-body py-4">
            {showMobileCards ? (
              <div className={styles.mobileCardList}>
                {table.getRowModel().rows.length === 0 && (
                  <div className="text-center text-muted py-4">
                    {noRowsMessage ?? intl.formatMessage({id: 'TABLE.NO_ROWS'})}
                  </div>
                )}
                {table.getRowModel().rows.map((row) => {
                  const isClickable = rowClickable ? rowClickable(row.original) : false;
                  return (
                    <div
                      key={row.id}
                      className={clsx(styles.mobileCard, isClickable && styles.mobileCardClickable)}
                      onClick={() => {
                        if (isClickable && onRowClick) {
                          onRowClick(row.original);
                        }
                      }}
                    >
                      {renderMobileCard?.(row.original)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <table
                className={clsx(
                  'table table-striped align-middle table-row-dashed fs-5 dataTable',
                  styles.ynex_table
                )}
                style={{tableLayout: 'fixed'}}
              >
                {(caption ?? title) && (
                  <caption className="visually-hidden">{caption ?? title}</caption>
                )}
                <colgroup>
                  {table.getAllColumns().map((column) => (
                    <col
                      key={column.id}
                      style={
                        column.columnDef.size !== undefined
                          ? {width: column.columnDef.size}
                          : undefined
                      }
                    />
                  ))}
                </colgroup>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr className="text-muted fw-bolder fs-5 gs-0" key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHeader header={header} key={header.id} />
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="fw-semibold">
                  {table.getRowModel().rows.length === 0 && (
                    <tr>
                      <td className="text-center" colSpan={table.getVisibleLeafColumns().length}>
                        {noRowsMessage ?? intl.formatMessage({id: 'TABLE.NO_ROWS'})}
                      </td>
                    </tr>
                  )}
                  {table.getRowModel().rows.map((row) => {
                    const rowDetail = renderRowDetail ? renderRowDetail(row.original) : null;
                    const isExpanded = rowDetail != null && expandedRowIds.has(row.id);
                    return (
                      <Fragment key={row.id}>
                        <tr className={rowClickable ? 'hoverable-row' : undefined}>
                          {row.getVisibleCells().map((cell) => {
                            const isCellClickable = rowClickable
                              ? rowClickable(row.original)
                              : false;
                            const cellClassName = (
                              cell.column.columnDef.meta as {noClick?: boolean}
                            )?.noClick
                              ? 'no-click'
                              : isCellClickable
                              ? 'clickable-cell'
                              : undefined;

                            return (
                              <td
                                key={cell.id}
                                className={cellClassName}
                                onClick={() => {
                                  if (
                                    isCellClickable &&
                                    !cellClassName?.includes('no-click') &&
                                    onRowClick
                                  ) {
                                    onRowClick(row.original);
                                  }
                                }}
                                style={
                                  isCellClickable && !cellClassName?.includes('no-click')
                                    ? {cursor: 'pointer'}
                                    : undefined
                                }
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td
                              colSpan={row.getVisibleCells().length}
                              className="p-0 bg-light bg-opacity-50"
                            >
                              {rowDetail}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {pagination && (manualPagination ? (rowCount ?? 0) > 0 : table.getPrePaginationRowModel().rows.length > 0) && (
              <div className={clsx('d-flex flex-stack flex-wrap pt-5', styles.paginationWrapper)} >
                <PaginationDetail
                  pagination={table.getState().pagination}
                  totalNumber={manualPagination ? (rowCount ?? 0) : table.getPrePaginationRowModel().rows.length}
                  elementName={elementName}
                  elementNamePlural={elementNamePlural}
                />

                <Pagination<T> table={table} />

                {pageSize === undefined && !manualPagination && (
                  <div className="d-flex justify-content-end fs-6 fw-semibold text-gray-700">
                    <TablePageSizeSelector
                      pageSizeState={table.getState().pagination.pageSize}
                      setPageSize={(selectedValue) => table.setPageSize(selectedValue)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TableMetronic;
